import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { Pin, PinOff, ChevronLeft } from "lucide-react";
import { getNextPanelZIndex } from "../../utils/panelZIndex";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useMobilePinnedToolPanelStore } from "../../stores/mobilePinnedToolPanelStore";
import Tooltip from "../ui/Tooltip";
import {
  getDefaultPanelSpawnPosition,
  getDefaultPanelInitialSize,
  getNextPanelSpawnPosition,
  MAIN_PANEL_DRAG_BUFFER,
  MAIN_PANEL_HEADER_RESERVED_HEIGHT,
  MAIN_PANEL_RESERVED_WIDTH,
} from "../../utils/panelSpawnConfig";

const StablePanelContent = React.memo(function StablePanelContent({ children }) {
  return children;
});

const EDGE_SNAP_THRESHOLD = 24;
const MAIN_PANEL_SNAP_THRESHOLD = 44;
const SNAP_TRANSITION_MS = 320;
const SNAP_TRANSITION = `left ${SNAP_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), top ${SNAP_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;

function restoreFocusToElement(element) {
  if (!(element instanceof HTMLElement)) return false;
  if (!document.contains(element)) return false;
  element.focus({ preventScroll: true });
  return document.activeElement === element;
}

const DraggablePanel = ({
  open = true,
  onClose,
  title = "",
  titleIcon: TitleIcon = null,
  /** Optional node after the title (e.g. help icon). Use `data-no-panel-drag` on interactive controls. */
  titleSuffix = null,
  children,
  initialPosition = getDefaultPanelSpawnPosition(),
  initialSize = null,
  minWidth = 250,
  minHeight = 300,
  maxWidth,
  maxHeight,
  rightButton = null,
  minimized = false,
  minimizedTargetPosition = null,
  /** Inspector-style panel: use the same transform/opacity timing as the main menu slide. */
  smoothMainMenuStyleSlide = false,
  /** When minimized, slide off the left edge (e.g. docked mobile sheet) instead of toward `minimizedTargetPosition`. */
  minimizedExitToLeft = false,
  showHeaderBar = true,
  /** When set, marks the full panel chrome (including header) for library drag cancel hit-testing. */
  libraryPanelRoot = false,
  /** When set, mesh-asset library drags skip scene drop preview/placement while the pointer is over this panel. */
  propertiesPanelRoot = false,
  /** Merged onto panel root (e.g. modifier classes that exempt specific panels from global responsive overrides). */
  rootExtraClassName = "",
  /** Optional chrome rendered just outside the bottom edge of an overflow-visible panel. */
  bottomOutsideContent = null,
  /** Keep the panel anchored to its initial right offset across viewport changes until manually dragged. */
  keepRightAnchoredUntilMoved = false,
  /**
   * When true, the panel root uses `overflow-visible` so outer rings/glows (e.g. duplicate feedback
   * on the inspector) are not clipped. Inner content still uses `overflow-hidden` on the body column.
   */
  allowRootOverflowVisible = false,
  /**
   * Mobile-only: when set, show a header pin control to expose this panel on the left edge tab strip.
   * @type {import("../../constants/mobileToolPanelPins").MobileToolPanelPinId | null | undefined}
   */
  mobilePinPanelId = null,
  /**
   * Optional ref whose `.current` is set to `(size: { width?: number; height?: number }) => void`
   * so parents can resize the panel (e.g. fit embedded content). Values are clamped to min/max and viewport.
   * @type {{ current: null | ((size: { width?: number; height?: number }) => void) } | null | undefined}
   */
  setPanelSizeRef = null,
  /**
   * When the panel opens and the user hasn’t dragged yet, place it here instead of the staggered spawn grid.
   * @type {{ x: number; y: number } | null | undefined}
   */
  openPosition = null,
  /**
   * When true, skip snapping away from / overlap checks with the main menu reserved region (e.g. full-width mobile sheets).
   */
  skipMainPanelOverlap = false,
}) => {
  const isMobile = useIsMobile();
  const pinnedPanelId = useMobilePinnedToolPanelStore((s) => s.pinnedPanelId);
  const togglePinSelection = useMobilePinnedToolPanelStore((s) => s.actions.togglePinSelection);
  const [pinFeedback, setPinFeedback] = useState(null);
  const pinWasPinnedRef = useRef(null);
  const initialPanelSizeRef = useRef(initialSize || getDefaultPanelInitialSize());
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialPanelSizeRef.current);
  const sizeRef = useRef(size);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const initialPositionRef = useRef({ x: 0, y: 0 });
  const panelRef = useRef(null);
  const zIndexRef = useRef(getNextPanelZIndex());
  const initialRightOffsetRef = useRef(
    Math.max(0, window.innerWidth - initialPosition.x - initialPanelSizeRef.current.width)
  );
  const previousOpenRef = useRef(open);
  const previousActiveElementRef = useRef(null);
  const hasUserMovedRef = useRef(false);
  const hasUserResizedRef = useRef(false);
  const swipeToCloseHandlers = useSwipeToClose({
    enabled: open && !minimized,
    onClose,
  });
  /** Match inspector mobile sheet: slide off left + eased opacity (see index.css exemption). */
  const mobilePinnedSheet = Boolean(isMobile && mobilePinPanelId);
  const effectiveSmoothMainMenuSlide = smoothMainMenuStyleSlide || mobilePinnedSheet;

  useEffect(() => {
    if (!isMobile || !mobilePinPanelId) {
      pinWasPinnedRef.current = null;
      return;
    }
    const nowPinned = pinnedPanelId === mobilePinPanelId;
    const prev = pinWasPinnedRef.current;
    if (prev === null) {
      pinWasPinnedRef.current = nowPinned;
      return;
    }
    pinWasPinnedRef.current = nowPinned;
    if (nowPinned && !prev) {
      setPinFeedback("Pinned!");
      const id = window.setTimeout(() => setPinFeedback(null), 1600);
      return () => window.clearTimeout(id);
    }
  }, [isMobile, mobilePinPanelId, pinnedPanelId]);

  const mobilePinShowsUnpin =
    Boolean(isMobile && mobilePinPanelId) && pinnedPanelId === mobilePinPanelId;
  const PinActionIcon = mobilePinShowsUnpin ? PinOff : Pin;

  const bringToFront = useCallback(() => {
    const next = getNextPanelZIndex();
    zIndexRef.current = next;
    if (panelRef.current) {
      panelRef.current.style.zIndex = String(next);
    }
  }, []);

  // Edge snapping system
  const margin = 10;
  const mainPanelWidth = MAIN_PANEL_RESERVED_WIDTH;
  const mainPanelHeight = MAIN_PANEL_HEADER_RESERVED_HEIGHT;
  const mainPanelRightMargin = MAIN_PANEL_DRAG_BUFFER;

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  const getPanelViewportBounds = useCallback(() => {
    const viewportWidth = maxWidth || window.innerWidth;
    const viewportHeight = maxHeight || window.innerHeight;
    const availableWidth = Math.max(1, viewportWidth - margin * 2);
    const availableHeight = Math.max(1, viewportHeight - margin * 2);
    return {
      viewportWidth,
      viewportHeight,
      availableWidth,
      availableHeight,
      effectiveMinWidth: Math.min(minWidth, availableWidth),
      effectiveMinHeight: Math.min(minHeight, availableHeight),
    };
  }, [margin, maxWidth, maxHeight, minWidth, minHeight]);

  const clampPanelSizeToViewport = useCallback(
    (panelSize) => {
      const { availableWidth, availableHeight, effectiveMinWidth, effectiveMinHeight } =
        getPanelViewportBounds();
      return {
        width: Math.max(effectiveMinWidth, Math.min(panelSize.width, availableWidth)),
        height: Math.max(effectiveMinHeight, Math.min(panelSize.height, availableHeight)),
      };
    },
    [getPanelViewportBounds]
  );

  const constrainPositionForSize = useCallback(
    (x, y, panelSize) => {
      const { viewportWidth, viewportHeight } = getPanelViewportBounds();
      const maxX = viewportWidth - panelSize.width - margin;
      const maxY = viewportHeight - panelSize.height - margin;
      let constrainedX = Math.max(margin, Math.min(x, maxX));
      const constrainedY = Math.max(margin, Math.min(y, maxY));
      const canAvoidMainPanel =
        mainPanelWidth + mainPanelRightMargin + panelSize.width + margin <= viewportWidth;
      if (
        canAvoidMainPanel &&
        !skipMainPanelOverlap &&
        constrainedX < mainPanelWidth + mainPanelRightMargin &&
        constrainedY < mainPanelHeight + margin
      ) {
        constrainedX = mainPanelWidth + mainPanelRightMargin;
      }
      return { x: constrainedX, y: constrainedY };
    },
    [
      getPanelViewportBounds,
      margin,
      skipMainPanelOverlap,
      mainPanelWidth,
      mainPanelRightMargin,
      mainPanelHeight,
    ]
  );

  // Constrain position to viewport and main panel
  const constrainToViewport = useCallback(
    (x, y) => constrainPositionForSize(x, y, size),
    [constrainPositionForSize, size]
  );

  // Snap to edges and main panel
  const snapToEdges = useCallback(
    (x, y) => {
      const maxX = (maxWidth || window.innerWidth) - size.width - margin;
      const maxY = (maxHeight || window.innerHeight) - size.height - margin;
      let snappedX = x;
      let snappedY = y;
      const mainPanelSnapX = mainPanelWidth + mainPanelRightMargin;
      const canAvoidMainPanel =
        mainPanelSnapX + size.width + margin <= (maxWidth || window.innerWidth);
      if (Math.abs(x - margin) < EDGE_SNAP_THRESHOLD) snappedX = margin;
      if (Math.abs(x - maxX) < EDGE_SNAP_THRESHOLD) snappedX = maxX;
      if (Math.abs(y - margin) < EDGE_SNAP_THRESHOLD) snappedY = margin;
      if (Math.abs(y - maxY) < EDGE_SNAP_THRESHOLD) snappedY = maxY;
      if (
        canAvoidMainPanel &&
        !skipMainPanelOverlap &&
        Math.abs(x - mainPanelSnapX) < MAIN_PANEL_SNAP_THRESHOLD
      )
        snappedX = mainPanelSnapX;
      if (!skipMainPanelOverlap && Math.abs(y - (mainPanelHeight + margin)) < EDGE_SNAP_THRESHOLD)
        snappedY = mainPanelHeight + margin;
      return constrainToViewport(snappedX, snappedY);
    },
    [
      maxWidth,
      maxHeight,
      size.width,
      size.height,
      margin,
      skipMainPanelOverlap,
      mainPanelWidth,
      mainPanelRightMargin,
      mainPanelHeight,
      constrainToViewport,
    ]
  );

  // Handle drag start
  const handleDragStart = (e) => {
    if (
      e.target.closest(
        "button, input, textarea, select, [contenteditable='true'], [data-no-panel-drag='true']"
      )
    ) {
      return;
    }
    e.preventDefault();
    hasUserMovedRef.current = true;
    bringToFront();
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    initialPositionRef.current = { x: position.x, y: position.y };
    document.addEventListener("mousemove", handleDrag, { passive: false });
    document.addEventListener("mouseup", handleDragEnd);
    if (panelRef.current) {
      panelRef.current.style.transition = "none";
      panelRef.current.style.cursor = "grabbing";
    }
  };

  // Handle drag
  const handleDrag = useCallback(
    (e) => {
      e.preventDefault();
      if (panelRef.current) {
        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;
        const newX = initialPositionRef.current.x + deltaX;
        const newY = initialPositionRef.current.y + deltaY;
        const maxX = (maxWidth || window.innerWidth) - size.width - margin;
        const maxY = (maxHeight || window.innerHeight) - size.height - margin;
        let constrainedX = Math.max(margin, Math.min(newX, maxX));
        const constrainedY = Math.max(margin, Math.min(newY, maxY));
        const wouldOverlapX = constrainedX < mainPanelWidth + mainPanelRightMargin;
        const wouldOverlapY = constrainedY < mainPanelHeight + margin;
        const canAvoidMainPanel =
          mainPanelWidth + mainPanelRightMargin + size.width + margin <=
          (maxWidth || window.innerWidth);
        if (canAvoidMainPanel && !skipMainPanelOverlap && wouldOverlapX && wouldOverlapY) {
          constrainedX = mainPanelWidth + mainPanelRightMargin;
        }
        panelRef.current.style.left = `${constrainedX}px`;
        panelRef.current.style.top = `${constrainedY}px`;
      }
    },
    [
      size.width,
      size.height,
      margin,
      mainPanelWidth,
      mainPanelHeight,
      mainPanelRightMargin,
      maxWidth,
      maxHeight,
      skipMainPanelOverlap,
    ]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("mouseup", handleDragEnd);
    const currentX = parseInt(panelRef.current?.style.left || position.x);
    const currentY = parseInt(panelRef.current?.style.top || position.y);
    const snappedPosition = snapToEdges(currentX, currentY);
    if (panelRef.current) {
      panelRef.current.style.transition = SNAP_TRANSITION;
      panelRef.current.style.left = `${snappedPosition.x}px`;
      panelRef.current.style.top = `${snappedPosition.y}px`;
      panelRef.current.style.cursor = "default";
    }
    setPosition(snappedPosition);
    setTimeout(() => {
      if (panelRef.current) {
        panelRef.current.style.transition = "opacity 0.3s ease";
      }
    }, SNAP_TRANSITION_MS);
  }, [handleDrag, position, snapToEdges]);

  // Handle resize start
  const handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    hasUserResizedRef.current = true;
    setIsResizing(true);
  };

  // Handle resize
  const handleResize = useCallback(
    (e) => {
      if (!isResizing) return;
      e.preventDefault();
      const newWidth = e.clientX - position.x;
      const newHeight = e.clientY - position.y;
      const maxW = (maxWidth || window.innerWidth) - position.x - 20;
      const maxH = (maxHeight || window.innerHeight) - position.y - 20;
      const resizeMinWidth = Math.min(minWidth, Math.max(1, maxW));
      const resizeMinHeight = Math.min(minHeight, Math.max(1, maxH));
      setSize({
        width: Math.max(resizeMinWidth, Math.min(newWidth, maxW)),
        height: Math.max(resizeMinHeight, Math.min(newHeight, maxH)),
      });
    },
    [isResizing, position, minWidth, minHeight, maxWidth, maxHeight]
  );

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add/remove resize listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResize, {
        passive: false,
      });
      document.addEventListener("mouseup", handleResizeEnd);
      return () => {
        document.removeEventListener("mousemove", handleResize);
        document.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [isResizing, handleResize, handleResizeEnd]);

  useLayoutEffect(() => {
    const justOpened = open && !previousOpenRef.current;
    const justClosed = !open && previousOpenRef.current;
    if (justOpened) {
      previousActiveElementRef.current = document.activeElement;
      bringToFront();
      if (!hasUserMovedRef.current) {
        const next = openPosition != null ? openPosition : getNextPanelSpawnPosition();
        setPosition(constrainToViewport(next.x, next.y));
      }
    }
    if (justClosed) {
      const previousActiveElement = previousActiveElementRef.current;
      const activeElement = document.activeElement;
      const focusIsInsidePanel =
        activeElement instanceof HTMLElement && panelRef.current?.contains(activeElement);

      if (focusIsInsidePanel || activeElement === document.body) {
        restoreFocusToElement(previousActiveElement);
      }
      previousActiveElementRef.current = null;
    }
    previousOpenRef.current = open;
  }, [open, bringToFront, openPosition, constrainToViewport]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const handleViewportResize = () => {
      const targetSize = hasUserResizedRef.current ? sizeRef.current : initialPanelSizeRef.current;
      const nextSize = clampPanelSizeToViewport(targetSize);
      sizeRef.current = nextSize;
      setSize(nextSize);
      setPosition((currentPosition) => {
        const nextX =
          keepRightAnchoredUntilMoved && !hasUserMovedRef.current
            ? window.innerWidth - nextSize.width - initialRightOffsetRef.current
            : currentPosition.x;
        return constrainPositionForSize(nextX, currentPosition.y, nextSize);
      });
    };

    handleViewportResize();
    window.addEventListener("resize", handleViewportResize);
    window.visualViewport?.addEventListener("resize", handleViewportResize);
    return () => {
      window.removeEventListener("resize", handleViewportResize);
      window.visualViewport?.removeEventListener("resize", handleViewportResize);
    };
  }, [open, clampPanelSizeToViewport, constrainPositionForSize, keepRightAnchoredUntilMoved]);

  const applyProgrammaticPanelSize = useCallback(
    (next) => {
      setSize((prev) => {
        const maxW = (maxWidth || window.innerWidth) - position.x - margin;
        const maxH = (maxHeight || window.innerHeight) - position.y - margin;
        return {
          width:
            next.width !== undefined
              ? Math.max(Math.min(minWidth, maxW), Math.min(Math.round(next.width), maxW))
              : prev.width,
          height:
            next.height !== undefined
              ? Math.max(Math.min(minHeight, maxH), Math.min(Math.round(next.height), maxH))
              : prev.height,
        };
      });
    },
    [minWidth, minHeight, maxWidth, maxHeight, position.x, position.y]
  );

  useLayoutEffect(() => {
    if (!setPanelSizeRef) return;
    setPanelSizeRef.current = applyProgrammaticPanelSize;
    return () => {
      setPanelSizeRef.current = null;
    };
  }, [setPanelSizeRef, applyProgrammaticPanelSize]);

  return (
    <div
      ref={panelRef}
      {...(libraryPanelRoot ? { "data-library-panel-root": "" } : {})}
      {...(propertiesPanelRoot ? { "data-properties-panel-root": "" } : {})}
      {...swipeToCloseHandlers}
      className={[
        `draggable-panel fixed z-30 border border-gray-700 rounded-lg shadow-xl flex flex-col ${
          allowRootOverflowVisible ? "overflow-visible" : "overflow-hidden"
        } ${
          open && !minimized ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`,
        mobilePinnedSheet ? "draggable-panel--mobile-pinned-sheet" : "",
        rootExtraClassName,
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseDownCapture={() => {
        if (open) bringToFront();
      }}
      style={{
        backgroundColor: "var(--panel-surface)",
        backdropFilter: "blur(10px)",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        cursor: "default",
        transform: minimized
          ? minimizedExitToLeft
            ? "translateX(calc(-100% - 12px))"
            : (() => {
                if (!minimizedTargetPosition) return "translateX(calc(100% + 20px))";
                const panelCenterX = position.x + size.width / 2;
                const panelCenterY = position.y + size.height / 2;
                const dx = minimizedTargetPosition.x - panelCenterX;
                const dy = minimizedTargetPosition.y - panelCenterY;
                const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                const slide = Math.max(window.innerWidth, window.innerHeight) * 1.2;
                return `translate(${(dx / dist) * slide}px, ${(dy / dist) * slide}px) scale(0.15)`;
              })()
          : open
            ? "translateX(0)"
            : mobilePinnedSheet
              ? "translateX(calc(-100% - 12px))"
              : "translateX(-12px)",
        transition: effectiveSmoothMainMenuSlide
          ? "opacity 0.25s ease-in-out, transform 0.25s ease-in-out"
          : minimized
            ? "opacity 0.2s ease, transform 0.25s ease-in-out"
            : open
              ? "transform 0.06s linear"
              : "opacity 0.08s linear, transform 0.08s linear",
        minWidth: getPanelViewportBounds().effectiveMinWidth,
        minHeight: getPanelViewportBounds().effectiveMinHeight,
        maxWidth: maxWidth || undefined,
        maxHeight: maxHeight || undefined,
        zIndex: zIndexRef.current,
      }}
    >
      {/* Draggable header */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-gray-700 flex-shrink-0 cursor-grab active:cursor-grabbing select-none"
        style={{ backgroundColor: showHeaderBar ? "rgba(58, 58, 62, 1)" : "transparent" }}
        onMouseDown={handleDragStart}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {TitleIcon ? (
            <TitleIcon className="h-4 w-4 flex-shrink-0 text-gray-200" aria-hidden="true" />
          ) : null}
          <h2 className="min-w-0 truncate text-base leading-tight font-semibold text-gray-100 select-none">
            {title}
          </h2>
          {titleSuffix ? <span className="flex-shrink-0">{titleSuffix}</span> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {isMobile && mobilePinPanelId ? (
            <div className="relative">
              <Tooltip
                content={
                  mobilePinShowsUnpin ? "Unpin from edge tab" : "Pin to edge tab for quick access"
                }
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePinSelection(mobilePinPanelId);
                  }}
                  className={`flex h-8 w-8 items-center justify-center rounded-md ${
                    mobilePinShowsUnpin
                      ? "text-white hover:text-gray-200"
                      : "text-gray-400 hover:text-white"
                  }`}
                  style={{ backgroundColor: "transparent", border: "none", outline: "none" }}
                  aria-label={
                    mobilePinShowsUnpin ? "Unpin from edge tab" : "Pin to edge tab for quick access"
                  }
                  aria-pressed={mobilePinShowsUnpin}
                >
                  <PinActionIcon className="h-4 w-4 flex-shrink-0" strokeWidth={2} aria-hidden />
                </button>
              </Tooltip>
              {pinFeedback ? (
                <span
                  role="status"
                  aria-live="polite"
                  className="pointer-events-none absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2 whitespace-nowrap text-xs font-medium text-white"
                >
                  {pinFeedback}
                </span>
              ) : null}
            </div>
          ) : null}
          {rightButton ? (
            rightButton
          ) : (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="draggable-panel-close-button text-gray-400 text-xl px-1.5 py-0.5 rounded cursor-pointer select-none leading-none"
              style={{
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
              }}
              aria-label={isMobile ? "Collapse panel" : "Close panel"}
            >
              {isMobile ? (
                <ChevronLeft className="h-5 w-5 flex-shrink-0" strokeWidth={2.5} aria-hidden />
              ) : (
                <span aria-hidden>&times;</span>
              )}
            </button>
          )}
        </div>
      </div>
      {/* Content area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <StablePanelContent>{children}</StablePanelContent>
      </div>
      {bottomOutsideContent ? (
        <div className="pointer-events-none absolute left-0 right-0 top-full z-20">
          <div className="pointer-events-auto">{bottomOutsideContent}</div>
        </div>
      ) : null}
      {/* Resize handle */}
      <div
        className="draggable-panel-resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
        onMouseDown={handleResizeStart}
        style={{
          background: "linear-gradient(135deg, transparent 50%, #6b7280 50%)",
        }}
      />
    </div>
  );
};

export default DraggablePanel;
